/**
 * COUNTRY SETTINGS — the pure rules behind the letterhead write surface (A3).
 *
 * A SEPARATE FILE FOR THE MEASURED REASON `golive-model.ts` IS ONE: react-refresh's rule
 * fires on any module exporting both a component and a value, and `CountrySettingsPage.tsx`
 * exporting these three took the web lint from 19 warnings to 22 on the first run. The
 * ceiling is a RATCHET, so the functions move out rather than the number moving up.
 *
 * It also puts the claims where they can be argued about on their own, which they
 * deserve — each is a decision that would be a live defect if taken the other way:
 *
 *   · {@link changedFields} sends a DIFF. All six countries hold a placeholder address
 *     today, so a snapshot body would 422 an operator who only corrected the GSTIN.
 *   · {@link fieldOfRefusal} translates the server's COLUMN into the screen's INPUT, over
 *     two wire shapes that were both MEASURED off the live route rather than read off the
 *     controller — one of them is not what the controller implies.
 *   · {@link formOf} is the null/empty-string translation, in one direction; the other
 *     direction lives in {@link changedFields} so the pair cannot drift.
 *   · {@link homeStatePairDefect} is the ONE server constraint this screen refuses to
 *     even offer — see the function for why it is the one, and why the split-group
 *     refusal deliberately is NOT a second.
 *
 * `country-settings.test.tsx` drives each in isolation AND through the screen.
 */

import { ApiRequestError } from '../../lib/api';
import type { CountryDto, CountryPatchBody } from './infra-api';

/**
 * The columns this screen writes. Everything below is keyed by this union.
 *
 * The home-state pair joined on the owner ruling of 2026-08-26 (recorded in full on
 * `CountrySettingsPage.tsx`): the C2 hint in the quote builder points a coordinator at
 * Country settings for exactly this pair, and a hint pointing at a screen where the field
 * does not exist is a dead end.
 */
export type EditableField =
  | 'legal_entity'
  | 'address_block'
  | 'tax_registration'
  | 'home_state_code'
  | 'home_state_name'
  /*
   * THE SAF-T(AO) HEADER — `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 U0 (owner "go"
   * 2026-09-25): the six columns the "Fiscal identity (AGT)" section round-trips for a
   * country whose row carries `saft_ao`. The flag itself is NOT editable and not here.
   */
  | 'agt_validation_no'
  | 'commercial_registry'
  | 'city'
  | 'province'
  | 'postal_code'
  | 'cae';

export type CountryForm = Record<EditableField, string>;

/**
 * The row as a form. `tax_registration` is `null` on the wire for every country today and
 * `''` in an input — one direction of the same translation {@link toBody} does back. The
 * home-state pair gets the identical treatment for the identical reason: nullable columns
 * where NULL is how "not supplied" is spelled, `''` is what an empty input holds. The six
 * header columns (U0) are nullable for the same reason and take the same treatment.
 */
export function formOf(row: CountryDto): CountryForm {
  return {
    legal_entity: row.legal_entity,
    address_block: row.address_block,
    tax_registration: row.tax_registration ?? '',
    home_state_code: row.home_state_code ?? '',
    home_state_name: row.home_state_name ?? '',
    agt_validation_no: row.agt_validation_no ?? '',
    commercial_registry: row.commercial_registry ?? '',
    city: row.city ?? '',
    province: row.province ?? '',
    postal_code: row.postal_code ?? '',
    cae: row.cae ?? '',
  };
}

/** The nullable columns that take the `'' → null` translation in {@link changedFields}. */
const NULLABLE_FIELDS: readonly Exclude<EditableField, 'legal_entity' | 'address_block'>[] = [
  'tax_registration',
  'home_state_code',
  'home_state_name',
  'agt_validation_no',
  'commercial_registry',
  'city',
  'province',
  'postal_code',
  'cae',
];

/**
 * WHAT ACTUALLY CHANGED — the body of the PATCH.
 *
 * ⚠ COMPARED AGAINST THE LOADED ROW, CHARACTER FOR CHARACTER, WITH NO TRIMMING. `address_block`
 * is printed verbatim on documents and `countries.dto.ts` says newlines are meaningful there,
 * so a client that quietly trimmed would be a second, invisible author of a customer-facing
 * value. Leading whitespace an operator typed is theirs; the server's predicates decide
 * whether it amounts to ink.
 *
 * ⚠ AND AN EMPTY `tax_registration` IS SENT AS `null`, NOT `''`. The column is nullable and
 * NULL is what "not supplied yet" is spelled as in this database (measured: NULL on all six
 * rows). `''` would read as "supplied, and it is nothing" to every consumer — including
 * `readiness.ts` `isSupplied`, which would still call it unsatisfied, and a human reading
 * the row, who would not know which.
 *
 * ⚠ THE HOME-STATE PAIR TAKES THE SAME `'' → null` TRANSLATION, AND FOR THE CODE IT IS
 * LOAD-BEARING TWICE OVER: `home_state_code` is `@Matches(/^\d{2}$/)` on the DTO and
 * `@IsOptional` skips only `undefined`/`null` — an `''` would FAIL the regex, so a screen
 * that sent the empty string could never clear the state at all. And `null` is the spelling
 * the split-group door judges (`countries.service.ts` `assertStateConsistency` fires on the
 * merged row losing its code), so the clear the API refuses under an active split group is
 * exactly the clear this translation produces — never a third, unrefusable spelling.
 *
 * ⚠ THE SIX HEADER COLUMNS (U0) TAKE THE SAME TRANSLATION, AND FOR THE TWO PATTERNED ONES
 * IT IS LOAD-BEARING THE WAY IT IS FOR THE STATE CODE: `agt_validation_no` is
 * `@Matches(/^\d+\/AGT\/\d{4}$/)` and `cae` is `@Matches(/^\d{5}$/)`, `@IsOptional` skips
 * only `undefined`/`null`, so an `''` would FAIL the pattern and a certificate number could
 * never be cleared. The loop below is the one place all nine nullable columns are spelled
 * — the letterhead pair stays explicit above it because those two are NOT NULL and take no
 * null at all.
 */
export function changedFields(original: CountryForm, next: CountryForm): CountryPatchBody {
  const body: CountryPatchBody = {};
  if (next.legal_entity !== original.legal_entity) body.legal_entity = next.legal_entity;
  if (next.address_block !== original.address_block) body.address_block = next.address_block;
  for (const field of NULLABLE_FIELDS) {
    if (next[field] !== original[field]) {
      body[field] = next[field] === '' ? null : next[field];
    }
  }
  return body;
}

/**
 * Which INPUT a server refusal belongs under, from the COLUMN it names.
 *
 * THREE ENVELOPES REACH HERE, AND THEY CARRY THE COLUMN IN DIFFERENT PLACES. Every shape
 * was MEASURED off the live route (`country-letterhead-write.e2e-spec.ts`,
 * `country-home-state-write.e2e-spec.ts`), not inferred from the controller — see the
 * violations one for why that distinction cost a bug.
 *
 *   · 422 `COUNTRY_LETTERHEAD_UNUSABLE`
 *         `details = { country: 'IN', field: 'countries.address_block', defect: 'unconfirmed' }`
 *     Thrown by `countries.service.ts` `assertLetterheadWritable` and, on the document
 *     path, by `quote-pdf.service.ts` `assertLetterheadUsable` — same code, same keys.
 *
 *   · 422 `VALIDATION_FAILED` with a BARE column in `details.field`
 *         `details = { field: 'home_state_code', reason: 'SPLIT_GROUP_NEEDS_HOME_STATE',
 *                      tax_group_ids: ['1'] }`
 *     The split-group door (`countries.service.ts` `assertStateConsistency`) refusing to
 *     clear the state while an active split-mode group exists. NO `countries.` prefix —
 *     which is why the prefix strip below is conditional — and the MESSAGE is the whole
 *     remedy (it names the holding groups and the ordering rule), so it is surfaced
 *     verbatim, never paraphrased. This page cannot see this refusal coming (the row it
 *     loads carries no groups — {@link homeStatePairDefect} records the decision), so
 *     landing it under the right input IS the doomed-request rule's honest fallback.
 *
 *   · 400 `VALIDATION_FAILED`
 *         `details = { violations: ['address_block must be shorter than or equal to 2000 characters'] }`
 *
 *     ⚠ THIS BRANCH WAS FIRST WRITTEN AGAINST THE WRONG SHAPE, AND THE E2E FOUND IT. The
 *     route attaches `crfValidationPipe()` per handler, which produces 422 with `details`
 *     flattened PER FIELD (`{address_block: ['…']}`) — so reading the controller gives you
 *     that shape and this function was built for it. MEASURED, the server answers 400 with
 *     `{violations: [...]}`, because the GLOBAL `ValidationPipe` (`main.ts:24`) runs FIRST
 *     over the same DTO metadata and the per-handler pipe never sees a validation error at
 *     all. A client shape derived from reading a controller is a guess; this one is a
 *     measurement.
 *
 *     The column is recovered from the START of the violation sentence, which is
 *     class-validator's own format (`<property> must be …` — the `home_state_code`
 *     Matches message keeps that opening on purpose) — an exact prefix match against
 *     the fields this screen owns, never a substring search, so a message merely
 *     MENTIONING a column cannot capture it.
 *
 * ⚠ AN UNRECOGNISED SHAPE RETURNS `null` AND THE CALLER SHOWS A PAGE-LEVEL ALERT. Guessing
 * a field would put a refusal under an input the operator then edits — worse than a banner,
 * because they would change something that was never wrong.
 */
export function fieldOfRefusal(error: unknown): { field: EditableField; message: string } | null {
  if (!(error instanceof ApiRequestError)) return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;

  const record = details as { field?: unknown; violations?: unknown };

  if (typeof record.field === 'string') {
    const named = record.field;
    const column = named.startsWith('countries.') ? named.slice('countries.'.length) : named;
    if (isEditableField(column)) return { field: column, message: error.message };
  }

  if (Array.isArray(record.violations)) {
    for (const line of record.violations) {
      if (typeof line !== 'string') continue;
      const field = EDITABLE_FIELDS.find((candidate) => line.startsWith(`${candidate} `));
      if (field !== undefined) return { field, message: line };
    }
  }
  return null;
}

const EDITABLE_FIELDS: readonly EditableField[] = [
  'legal_entity',
  'address_block',
  'tax_registration',
  'home_state_code',
  'home_state_name',
  'agt_validation_no',
  'commercial_registry',
  'city',
  'province',
  'postal_code',
  'cae',
];

function isEditableField(value: string): value is EditableField {
  return (EDITABLE_FIELDS as readonly string[]).includes(value);
}

/**
 * THE ONE DOOMED REQUEST THIS FORM CAN SEE COMING, refused before it is offered.
 *
 * `PATCH /countries/:cc` refuses a merged row holding `home_state_name` without
 * `home_state_code` (`countries.service.ts` `assertStateConsistency`: §9.2 compares state
 * CODES, never names). Unlike every other content rule the route enforces, this one is
 * decidable from NOTHING BUT THE FORM — both halves of the pair are on screen — so the
 * house rule applies in its strong form: a control must not offer a request the API will
 * refuse. The mirror is the merged-row predicate, transcribed: name has ink, code does not.
 *
 * ⚠ THE SPLIT-GROUP REFUSAL IS DELIBERATELY **NOT** MIRRORED HERE, AND THE DIFFERENCE IS
 * WHAT THE PAGE CAN KNOW. Whether an active `IN_GST_STATE_SPLIT` group exists lives in
 * `tax_groups`, which this page does not load — `CountrySettingsPage` reads
 * `GET /countries/:cc` and nothing else, and the country row does not carry its groups.
 * Predicting that refusal would mean a second query with its own loading, error and
 * staleness arms, purchased for a hint the server's own refusal already states better: the
 * 422 names the holding group(s) and the ordering rule, carries
 * `field: 'home_state_code'`, and {@link fieldOfRefusal} lands it under the input VERBATIM
 * (`country-home-state-write.e2e-spec.ts` measures that envelope). The letterhead gate set
 * this page's precedent — server-data predicates are surfaced, not predicted.
 *
 * The refusal sits UNDER `home_state_name` because that is the server's own subject
 * (*"home_state_name is set without home_state_code"*) and the field whose content makes
 * the pair inconsistent; the sentence still names both remedies.
 */
export function homeStatePairDefect(
  form: CountryForm,
): { field: EditableField; message: string } | null {
  if (form.home_state_name !== '' && form.home_state_code === '') {
    return {
      field: 'home_state_name',
      message:
        'A state name cannot be saved without its code — tax is resolved by comparing ' +
        'state codes, never names. Supply the two-digit code, or clear the name.',
    };
  }
  return null;
}
